import { NavLink as RouterNavLink, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import type { NavItem, RoleConfig } from "./roles";
import { getAdminSidebar } from "../api/endpoints";
import type { AdminSidebar as AdminSidebarCounts } from "../api/types";

// Маршруты, для которых известно, какой счётчик из /admin/sidebar показать.
// Если в будущем добавится новый раздел — расширяем маппинг и API-схему,
// а не правим JSX.
const ADMIN_BADGE_BY_PATH: Record<string, keyof AdminSidebarCounts> = {
  "/admin/apps": "apps_total",
  "/admin/users": "users_total",
  "/admin/groups": "groups_total",
  "/admin/locals": "locals_total",
};

export function Sidebar({
  role,
  asDrawer = false,
  onItemClick,
}: {
  role: RoleConfig;
  asDrawer?: boolean;
  onItemClick?: () => void;
}) {
  // Тянем счётчики только для admin-роли. Для local/dev/client — не дёргаем
  // эндпойнт (там бейджей с реальной БД пока всё равно нет).
  const isAdmin = role.role === "admin";
  const sidebarQ = useQuery({
    queryKey: ["admin", "sidebar"],
    queryFn: getAdminSidebar,
    enabled: isAdmin,
    refetchInterval: 30_000,
    staleTime: 15_000,
  });

  const adminCounts = isAdmin ? sidebarQ.data : undefined;
  const adminLoading = isAdmin && sidebarQ.isLoading && !sidebarQ.data;

  return (
    <div className={asDrawer ? "px-3 py-5" : ""}>
      {role.groups.map((g, i) => (
        <div key={g.title} className={i === 0 ? "" : "mt-7"}>
          <div className="px-3 pb-2 text-[10.5px] font-medium uppercase tracking-[0.08em] text-[var(--color-fg-faint)]">
            {g.title}
          </div>
          <ul className="flex flex-col gap-0.5">
            {g.items.map((item) => (
              <li key={item.to}>
                <NavRow
                  item={item}
                  onClick={onItemClick}
                  adminBadge={resolveAdminBadge(item, adminCounts, adminLoading)}
                />
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

export function StickySidebar({ role }: { role: RoleConfig }) {
  return (
    <aside className="sticky top-14 hidden h-[calc(100vh-3.5rem)] w-[244px] shrink-0 self-start overflow-y-auto border-r border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-6 md:block">
      <Sidebar role={role} />
    </aside>
  );
}

type AdminBadge = { kind: "value"; text: string } | { kind: "skeleton" } | null;

function resolveAdminBadge(
  item: NavItem,
  counts: AdminSidebarCounts | undefined,
  loading: boolean,
): AdminBadge {
  const key = ADMIN_BADGE_BY_PATH[item.to];
  if (!key) return null;
  if (counts) return { kind: "value", text: String(counts[key]) };
  if (loading) return { kind: "skeleton" };
  // нет данных и не грузится (например, эндпойнт ругнулся) — скрываем
  return null;
}

function NavRow({
  item,
  onClick,
  adminBadge,
}: {
  item: NavItem;
  onClick?: () => void;
  adminBadge: AdminBadge;
}) {
  const Icon = item.icon;
  const location = useLocation();
  // если matchPrefix — активна и при суб-роутах (карточка приложения)
  const active = item.matchPrefix
    ? location.pathname === item.to || location.pathname.startsWith(item.to + "/")
    : location.pathname === item.to;

  return (
    <RouterNavLink
      to={item.to}
      end={!item.matchPrefix}
      onClick={onClick}
      className={
        "group flex items-center gap-2.5 rounded-lg px-3 py-2 text-[13.5px] transition-all duration-150 " +
        (active
          ? "bg-[var(--color-surface-3)] font-medium text-[var(--color-fg)]"
          : "text-[var(--color-fg-muted)] hover:translate-x-0.5 hover:bg-[var(--color-surface-2)] hover:text-[var(--color-fg)]")
      }
    >
      <Icon size={16} className="opacity-80" />
      <span className="flex-1">{item.label}</span>
      <BadgeSlot itemBadge={item.badge} adminBadge={adminBadge} />
    </RouterNavLink>
  );
}

function BadgeSlot({
  itemBadge,
  adminBadge,
}: {
  itemBadge?: string;
  adminBadge: AdminBadge;
}) {
  // admin-бейдж имеет приоритет над статикой из roles.ts.
  if (adminBadge?.kind === "value") {
    return (
      <span className="font-mono text-[11px] text-[var(--color-fg-faint)] group-hover:text-[var(--color-fg-muted)]">
        {adminBadge.text}
      </span>
    );
  }
  if (adminBadge?.kind === "skeleton") {
    // Тонкая чёрточка-плейсхолдер, чтобы избежать прыжка 0 → реальное.
    return (
      <span
        aria-hidden
        className="inline-block h-[2px] w-5 rounded bg-[var(--color-fg-faint)] opacity-40"
      />
    );
  }
  if (itemBadge) {
    return (
      <span className="font-mono text-[11px] text-[var(--color-fg-faint)] group-hover:text-[var(--color-fg-muted)]">
        {itemBadge}
      </span>
    );
  }
  return null;
}
